import {
  AfterViewInit,
  Component,
  ContentChild,
  ElementRef,
  EventEmitter,
  inject,
  OnInit,
  Output,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { filter, take } from 'rxjs';
import { GabaritService } from '@services/gabarit/gabarit.service';
import { GlobalStoreService } from '@services/global-store/global-store.service';
import { GestionTitreService } from '@services/navigation/gestion-titre.service';
import { ResultatModificationCoordonneesMouvement } from 'src/app/api/model/resultat-modification-coordonnees-mouvement.model';
import { SystemeEntiteEnum } from 'src/app/constantes/systeme-entite.enum';
import { ContexteModificationEnum } from '../../constantes/contexte-modification.enum';
import { AccessibiliteService } from '@services/accessibilite.service';

@Component({
  selector: 'app-gabarit-confirmation',
  templateUrl: './gabarit-confirmation.component.html',
  styleUrl: './gabarit-confirmation.component.scss',
  standalone: false,
})
export class GabaritConfirmationComponent implements OnInit, AfterViewInit {
  isAppMobile?: boolean;
  afficherSuccesPartiel = false;
  afficherVarianteTextePartiel = false;
  secteurs: Array<string> = [];
  private readonly systemesEntiteValides = new Set<string>(Object.values(SystemeEntiteEnum));
  contexteModification: ContexteModificationEnum | null = null;
  @ContentChild('steps') refSteps?: ElementRef;
  @Output() systemesEnErreur: EventEmitter<SystemeEntiteEnum[]> = new EventEmitter<
    SystemeEntiteEnum[]
  >();

  private readonly accessibiliteService = inject(AccessibiliteService);
  private readonly gabaritService = inject(GabaritService);
  private readonly gestionTitreService = inject(GestionTitreService);
  private readonly globalStoreService = inject(GlobalStoreService);
  private readonly translateService = inject(TranslateService);

  cleTraductionSecteurCredit = 'confirmation.secteurs.credit';
  cleTraductionSecteurSecuriteFinanciere = 'confirmation.secteurs.securiteFinanciere';
  cleTraductionSecteurAssurances = 'confirmation.secteurs.assurances';
  cleTraductionSecteurCourtage = 'confirmation.secteurs.courtage';
  cleTraductionSecteurSignature = 'confirmation.secteurs.signature';
  cleTraductionSecteurGestionPrivee = 'confirmation.secteurs.gestionPrivee';
  cleTraductionSecteurValeursMobiliere = 'confirmation.secteurs.valeursMobilieres';
  cleEt = 'confirmation.partiel.et';

  secteurCredit: string = '';
  secteurSecuriteFinanciere: string = '';
  secteurAssurances: string = '';
  secteurCourtage: string = '';
  secteurSignature: string = '';
  secteurGestionPrivee: string = '';
  secteurValeursMobiliere: string = '';
  et: string = '';

  constructor() {}

  ngOnInit(): void {
    this.contexteModification = this.globalStoreService.getContexteModification();
    this.isAppMobile = this.gabaritService.isAppMobile();
    if (this.contexteModification === ContexteModificationEnum.ADRESSE) {
      this.gestionTitreService.addTitreOnglet('titreOnglet.pageConfirmationAdresse');
    } else {
      this.gestionTitreService.addTitreOnglet('titreOnglet.pageConfirmation');
    }
    this.initTraductions();

    this.globalStoreService
      .getConfirmationMouvement$()
      .pipe(
        filter((data) => !!data?.resultatsModification),
        take(1),
      )
      .subscribe((data) => {
        const resultatsModification = data?.resultatsModification ?? [];
        const resultatsModificationEnErreur = resultatsModification
          .filter((resultat) => resultat.succes === false)
          .sort(this.sortCreditPremier)
          .map((resultat) => {
            this.mapNomSecteur(resultat.systeme);
            return resultat;
          });

        const systemesEnErreur = Array.from(
          new Set(
            resultatsModificationEnErreur
              .map((resultat) => resultat.systeme)
              .filter((systeme): systeme is SystemeEntiteEnum => this.isSystemeEntiteEnum(systeme)),
          ),
        );

        this.afficherSuccesPartiel = this.secteurs.length > 0;
        this.afficherVarianteTextePartiel = resultatsModificationEnErreur.length > 0;
        this.systemesEnErreur.emit(systemesEnErreur);
      });
  }

  private isSystemeEntiteEnum(systeme: string | undefined): systeme is SystemeEntiteEnum {
    return !!systeme && this.systemesEntiteValides.has(systeme);
  }

  private initTraductions() {
    this.translateSecteur(this.cleTraductionSecteurCredit, 'secteurCredit');
    this.translateSecteur(this.cleTraductionSecteurSecuriteFinanciere, 'secteurSecuriteFinanciere');
    this.translateSecteur(this.cleTraductionSecteurAssurances, 'secteurAssurances');
    this.translateSecteur(this.cleTraductionSecteurCourtage, 'secteurCourtage');
    this.translateSecteur(this.cleTraductionSecteurSignature, 'secteurSignature');
    this.translateSecteur(this.cleTraductionSecteurGestionPrivee, 'secteurGestionPrivee');
    this.translateSecteur(this.cleTraductionSecteurValeursMobiliere, 'secteurValeursMobiliere');
    this.translateSecteur(this.cleEt, 'et');
  }

  private translateSecteur<K extends keyof GabaritConfirmationComponent>(cle: string, ref: K) {
    this.translateService.get(cle).subscribe((texte: string) => {
      (this[ref] as unknown as string) = texte;
    });
  }

  ngAfterViewInit(): void {
    this.accessibiliteService.setFocus('#titreConfirmation');
  }

  get afficherEtapes(): boolean {
    return this.refSteps?.nativeElement.childElementCount > 0 || this.afficherSuccesPartiel;
  }

  get contientPlusDUneEtape(): boolean {
    return (
      (this.refSteps?.nativeElement.childElementCount == 1 && this.afficherSuccesPartiel) ||
      this.refSteps?.nativeElement.childElementCount > 1
    );
  }

  get etapes(): HTMLElement[] {
    return Array.from(this.refSteps?.nativeElement.children || []);
  }

  get secteursTraduitsOrdonnees(): string {
    let secteurs = this.secteurs.slice(0, -1);
    let dernierSecteur = this.secteurs.at(-1) ?? '';
    if (this.secteurs.length === 1) {
      return dernierSecteur;
    } else {
      return secteurs.join(', ') + (dernierSecteur ? ` ${this.et} ${dernierSecteur}` : '');
    }
  }

  peutAfficherNousJoindre(el: HTMLElement): boolean {
    return !el.id.includes('no-contact');
  }

  sortCreditPremier(
    a: ResultatModificationCoordonneesMouvement,
    b: ResultatModificationCoordonneesMouvement,
  ) {
    if (a.systeme === SystemeEntiteEnum.CREDIT) return -1;
    if (b.systeme === SystemeEntiteEnum.CREDIT) return 1;
    return 0;
  }

  mapNomSecteur(nom: string | undefined): void {
    switch (nom) {
      case SystemeEntiteEnum.CREDIT:
        this.secteurs.push(this.secteurCredit);
        break;
      case SystemeEntiteEnum.COMPTE_SCGP:
        this.secteurs.push(
          this.secteurCourtage,
          this.secteurSignature,
          this.secteurGestionPrivee,
          this.secteurValeursMobiliere,
        );
        break;
      case SystemeEntiteEnum.AP:
        this.secteurs.push(this.secteurSecuriteFinanciere);
        break;
      case SystemeEntiteEnum.DGAG:
        if (this.contexteModification !== ContexteModificationEnum.ADRESSE) {
          this.secteurs.push(this.secteurAssurances);
        }
        break;
    }
  }
}
